import { NOTEBOOK_WORKSPACE_BG } from "@excalidraw/common";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  libraryApi,
  type CanvasMeta,
  type CanvasScene,
  type CanvasType,
  type Folder,
  type NotebookPageStyle,
} from "./libraryApi";
import {
  cacheLibrary,
  cacheScene,
  clearPending,
  enqueueOp,
  getCachedLibrary,
  getOps,
  getPendingIds,
  getPendingScene,
  setOps,
  type LibraryOp,
} from "./offlineStore";

// Offline-first repository for folders/canvases. The UI talks to this instead
// of libraryApi directly: reads fall back to the IndexedDB cache when offline,
// writes are applied optimistically to the cache + queued as idempotent ops
// (client-generated UUIDs) and synced to the server when the network is back.

type Library = { folders: Folder[]; canvases: CanvasMeta[] };

let cache: Library = { folders: [], canvases: [] };

const online = () => navigator.onLine !== false;

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const EMPTY_SCENE: CanvasScene = {
  type: "excalidraw",
  version: 2,
  source: "self-hosted",
  elements: [],
  appState: {},
  files: {},
};

const persist = () => cacheLibrary(cache.folders, cache.canvases);

const nextFolderPosition = (parentId: string | null) =>
  cache.folders
    .filter((f) => f.parent_id === parentId)
    .reduce((m, f) => Math.max(m, f.position), -1) + 1;

const nextCanvasPosition = (folderId: string | null) =>
  cache.canvases
    .filter((c) => c.folder_id === folderId)
    .reduce((m, c) => Math.max(m, c.position), -1) + 1;

// Re-add locally-created entities that the server doesn't know about yet (their
// create op is still queued). Without this, fetching the server list while
// online would silently DROP boards/folders created offline — and lose the
// metadata needed to (re)create them on sync.
const mergePendingCreates = async (
  folders: Folder[],
  canvases: CanvasMeta[],
): Promise<Library> => {
  const ops = await getOps();
  const folderIds = new Set(folders.map((f) => f.id));
  const canvasIds = new Set(canvases.map((c) => c.id));
  const now = Date.now();
  for (const op of ops) {
    if (op.kind === "createFolder" && !folderIds.has(op.id)) {
      folders.push({
        id: op.id,
        parent_id: op.parent_id,
        name: op.name,
        position: op.position,
        created_at: now,
        updated_at: now,
      });
      folderIds.add(op.id);
    } else if (op.kind === "createCanvas" && !canvasIds.has(op.id)) {
      canvases.push({
        id: op.id,
        folder_id: op.folder_id,
        name: op.name,
        description: null,
        position: op.position,
        created_at: now,
        updated_at: now,
        tags: [],
        canvas_type: (op.canvas_type as CanvasType) ?? "canvas",
        page_style: (op.page_style as NotebookPageStyle) ?? null,
      });
      canvasIds.add(op.id);
    }
  }
  return { folders, canvases };
};

// --- Reads ----------------------------------------------------------------
export const getLibrary = async (): Promise<Library> => {
  if (online()) {
    try {
      const [folders, canvases] = await Promise.all([
        libraryApi.listFolders(),
        libraryApi.listCanvases(),
      ]);
      cache = await mergePendingCreates(folders, canvases);
      void persist();
      return cache;
    } catch {
      /* offline / backend down → use cache */
    }
  }
  const cached = await getCachedLibrary();
  if (cached) {
    cache = cached;
  }
  return cache;
};

// --- Writes (optimistic + queued) -----------------------------------------
export const createFolder = async (input: {
  name: string;
  parent_id?: string | null;
}): Promise<Folder> => {
  const id = newId();
  const now = Date.now();
  const parent_id = input.parent_id ?? null;
  const folder: Folder = {
    id,
    parent_id,
    name: input.name,
    position: nextFolderPosition(parent_id),
    created_at: now,
    updated_at: now,
  };
  cache.folders.push(folder);
  void persist();
  await enqueueOp({
    kind: "createFolder",
    id,
    name: folder.name,
    parent_id,
    position: folder.position,
  });
  void syncNow();
  return folder;
};

export const createCanvas = async (input: {
  name: string;
  folder_id?: string | null;
  canvas_type?: CanvasType;
  page_style?: NotebookPageStyle;
}): Promise<CanvasMeta> => {
  const id = newId();
  const now = Date.now();
  const folder_id = input.folder_id ?? null;
  const canvas_type: CanvasType = input.canvas_type ?? "canvas";
  const page_style: NotebookPageStyle | null =
    canvas_type === "notebook" ? input.page_style ?? "ruled" : null;
  const canvas: CanvasMeta = {
    id,
    folder_id,
    name: input.name,
    description: null,
    position: nextCanvasPosition(folder_id),
    created_at: now,
    updated_at: now,
    tags: [],
    canvas_type,
    page_style,
  };
  cache.canvases.push(canvas);
  void persist();
  // Seed the local scene's appState so the notebook surface shows immediately
  // (and so the page count/style persist on the first save). For a plain canvas
  // the empty scene is used as-is.
  const seedScene: CanvasScene =
    canvas_type === "notebook"
      ? {
          ...EMPTY_SCENE,
          appState: {
            notebookMode: true,
            notebookPageStyle: page_style ?? "ruled",
            notebookPageCount: 1,
            notebookPageOpacity: 100,
            viewBackgroundColor: NOTEBOOK_WORKSPACE_BG,
          },
        }
      : EMPTY_SCENE;
  void cacheScene(id, seedScene);
  await enqueueOp({
    kind: "createCanvas",
    id,
    name: canvas.name,
    folder_id,
    position: canvas.position,
    canvas_type,
    ...(page_style ? { page_style } : {}),
  });
  void syncNow();
  return canvas;
};

type UpdateOp = Extract<LibraryOp, { kind: "updateFolder" | "updateCanvas" }>;

// Merge consecutive update ops on the same target to keep the queue small.
const enqueueUpdate = async (op: UpdateOp) => {
  const ops = await getOps();
  const last = ops[ops.length - 1];
  if (last && last.kind === op.kind && last.id === op.id && "body" in last) {
    (last as UpdateOp).body = { ...(last as UpdateOp).body, ...op.body };
    await setOps(ops);
  } else {
    await enqueueOp(op);
  }
};

export const updateFolder = async (
  id: string,
  body: Partial<{ name: string; parent_id: string | null; position: number }>,
): Promise<void> => {
  const f = cache.folders.find((x) => x.id === id);
  if (f) {
    Object.assign(f, body, { updated_at: Date.now() });
    void persist();
  }
  await enqueueUpdate({ kind: "updateFolder", id, body });
  void syncNow();
};

export const updateCanvas = async (
  id: string,
  body: Partial<{
    name: string;
    folder_id: string | null;
    position: number;
    description: string | null;
    page_style: NotebookPageStyle | null;
  }>,
): Promise<void> => {
  const c = cache.canvases.find((x) => x.id === id);
  if (c) {
    Object.assign(c, body, { updated_at: Date.now() });
    void persist();
  }
  await enqueueUpdate({ kind: "updateCanvas", id, body });
  void syncNow();
};

// Drop queued create/update ops for an id; only enqueue a delete if the entity
// had already been synced (i.e. no pending create). This makes "create offline
// then delete offline" a clean no-op.
const queueDelete = async (
  id: string,
  createKind: LibraryOp["kind"],
  updateKind: LibraryOp["kind"],
  deleteKind: "deleteFolder" | "deleteCanvas",
) => {
  const ops = await getOps();
  const hadPendingCreate = ops.some(
    (o) => o.kind === createKind && o.id === id,
  );
  const next = ops.filter(
    (o) => !((o.kind === createKind || o.kind === updateKind) && o.id === id),
  );
  if (!hadPendingCreate) {
    next.push({ kind: deleteKind, id } as LibraryOp);
  }
  await setOps(next);
};

export const deleteFolder = async (id: string): Promise<void> => {
  // local cascade: remove the folder + descendant folders; orphan their canvases
  const toRemove = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of cache.folders) {
      if (f.parent_id && toRemove.has(f.parent_id) && !toRemove.has(f.id)) {
        toRemove.add(f.id);
        grew = true;
      }
    }
  }
  cache.folders = cache.folders.filter((f) => !toRemove.has(f.id));
  for (const c of cache.canvases) {
    if (c.folder_id && toRemove.has(c.folder_id)) {
      c.folder_id = null;
    }
  }
  void persist();
  await queueDelete(id, "createFolder", "updateFolder", "deleteFolder");
  void syncNow();
};

export const deleteCanvas = async (id: string): Promise<void> => {
  cache.canvases = cache.canvases.filter((c) => c.id !== id);
  void persist();
  await queueDelete(id, "createCanvas", "updateCanvas", "deleteCanvas");
  void syncNow();
};

// --- Sync engine ----------------------------------------------------------
const is404 = (e: unknown) =>
  String(e instanceof Error ? e.message : e).includes("404");

const ignore404 = async (p: Promise<unknown>) => {
  try {
    await p;
  } catch (e) {
    if (!is404(e)) {
      throw e;
    }
  }
};

const sceneHasContent = (scene: CanvasScene): boolean =>
  Array.isArray(scene.elements) &&
  scene.elements.some(
    (el) =>
      el &&
      typeof el === "object" &&
      !(el as { isDeleted?: boolean }).isDeleted,
  );

// Make sure the canvas for an orphaned pending scene exists on the server so
// its scene can be pushed (create op lost / never ran). Recreates from local
// metadata when available; otherwise, if the scene still has real content,
// recreates a placeholder "recovered" board so the drawing is never lost.
// Returns false only when there's nothing worth keeping (empty + no metadata).
const ensureCanvasForOrphanScene = async (
  id: string,
  scene: CanvasScene,
): Promise<boolean> => {
  const meta = cache.canvases.find((c) => c.id === id);
  if (!meta && !sceneHasContent(scene)) {
    return false;
  }
  try {
    await libraryApi.createCanvas({
      id,
      name: meta?.name ?? t("offline.recoveredBoard"),
      folder_id: meta?.folder_id ?? null,
      position: meta?.position,
    });
    return true;
  } catch {
    return false;
  }
};

const applyOp = async (op: LibraryOp): Promise<void> => {
  switch (op.kind) {
    case "createFolder":
      await libraryApi.createFolder({
        id: op.id,
        name: op.name,
        parent_id: op.parent_id,
        position: op.position,
      });
      return;
    case "createCanvas":
      await libraryApi.createCanvas({
        id: op.id,
        name: op.name,
        folder_id: op.folder_id,
        position: op.position,
        canvas_type: op.canvas_type as CanvasType | undefined,
        page_style: op.page_style as NotebookPageStyle | undefined,
      });
      return;
    case "updateFolder":
      await ignore404(libraryApi.updateFolder(op.id, op.body));
      return;
    case "updateCanvas":
      await ignore404(
        libraryApi.updateCanvas(
          op.id,
          op.body as Partial<{
            name: string;
            folder_id: string | null;
            position: number;
            description: string | null;
            canvas_type: CanvasType;
          }>,
        ),
      );
      return;
    case "deleteFolder":
      await ignore404(libraryApi.deleteFolder(op.id));
      return;
    case "deleteCanvas":
      await ignore404(libraryApi.deleteCanvas(op.id));
  }
};

let syncing = false;

// Called before uploading a pending scene. Returns the scene that should be
// saved (possibly a merged version). Returning null means "use the local scene
// as-is". Implemented in App.tsx so it can access reconcileElements.
export type SceneReconciler = (
  id: string,
  localScene: CanvasScene,
) => Promise<CanvasScene | null>;

export const syncNow = async (
  reconcileScene?: SceneReconciler,
): Promise<void> => {
  if (syncing || !online()) {
    return;
  }
  syncing = true;
  try {
    // Make sure the in-memory cache reflects the durable offline cache, so the
    // self-heal below can recreate a canvas from its locally-stored metadata
    // even on a fresh load before getLibrary() has run.
    if (cache.folders.length === 0 && cache.canvases.length === 0) {
      const cached = await getCachedLibrary();
      if (cached) {
        cache = cached;
      }
    }

    // 1) replay structural ops (idempotent). A failing op only blocks further
    // ops for the SAME id (to preserve create→update→delete order); ops for
    // other ids keep flowing, so one poisoned op can't stall the whole queue.
    const ops = await getOps();
    const remaining: LibraryOp[] = [];
    const failedIds = new Set<string>();
    for (const op of ops) {
      if (failedIds.has(op.id)) {
        remaining.push(op);
        continue;
      }
      try {
        await applyOp(op);
      } catch {
        remaining.push(op);
        failedIds.add(op.id);
      }
    }
    await setOps(remaining);

    // 2) flush pending scenes. The canvas should exist now (its create op ran
    // above); if the scene PUT 404s, the create was lost/blocked — self-heal by
    // recreating the canvas from the local cache, then retry once.
    for (const id of await getPendingIds()) {
      const scene = await getPendingScene(id);
      if (!scene) {
        continue;
      }

      // Conflict-safe upload: reconcile with the server version if another
      // session has written to the canvas while we had it pending (e.g. Device A
      // offline while Device B edited online). Merges element-by-element so
      // changes from both sessions survive.
      let sceneToSave = scene;
      if (reconcileScene) {
        try {
          const merged = await reconcileScene(id, scene);
          if (merged) {
            sceneToSave = merged;
          }
        } catch {
          /* reconcile failed — upload local version */
        }
      }

      try {
        await libraryApi.saveScene(id, sceneToSave);
        await clearPending(id);
      } catch (e) {
        if (is404(e)) {
          if (await ensureCanvasForOrphanScene(id, scene)) {
            try {
              await libraryApi.saveScene(id, scene);
              await clearPending(id);
            } catch {
              /* keep pending */
            }
          } else {
            // empty orphan with no metadata → nothing to save; stop the
            // endless 404 retry loop by dropping the stale pending entry.
            await clearPending(id);
          }
        }
        /* else (non-404) keep pending for the next sync */
      }
    }

    // 3) reconcile the library cache with the server only when structural ops
    // ran (a scene-only flush doesn't change the folder/canvas list). This
    // keeps the periodic self-healing sync cheap (local IDB reads) when idle.
    if (ops.length > 0 && online()) {
      try {
        await getLibrary();
      } catch {
        /* ignore */
      }
    }
  } finally {
    syncing = false;
  }
};
